import type { FunActivityResponse, FunPaymentItem } from "@decibeltrade/utils";
import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import z from "zod/v4";

import { ERROR_BACKOFF_MS } from "@/contexts/queries/backoff";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { env } from "@/env";
import { useAuthState } from "@/hooks/use-auth-state";
import { Logger } from "@/utils/logger";
import {
  authenticatedRequest,
  AuthNotReadyError,
  type GetAuthHeaders,
} from "@/utils/signature-auth";

import { assertAnsweredFor, funRetry, isTerminalFunError, toFunPaymentError } from "./errors";
import { FunPaymentActivitySchema, isOpenFunPayment } from "./fun-payment-schemas";

const OPEN_POLL_MS = 10_000;
const WATCHING_POLL_MS = 60_000;

const isMovingFunPayment = (item: FunPaymentItem) =>
  item.state === "in_progress" || item.state === "delayed";

export const FunActivityEnvelopeSchema = z.object({
  success: z.literal(true),
  data: FunPaymentActivitySchema,
});

/** Prefix of every activity entry for the account, the show-all list included. */
export function funActivityQueryKey(accountAddress: string | undefined) {
  return ["funActivity", accountAddress] as const;
}

function funActivityAllQueryKey(accountAddress: string | undefined) {
  return [...funActivityQueryKey(accountAddress), "all"] as const;
}

interface ActivityFlags {
  all?: boolean;
  refresh?: boolean;
  clientSuccess?: boolean;
}

// False flags are omitted rather than sent as "false", which a coercing parser reads as true.
function activityUrl(flags: ActivityFlags) {
  const params = new URLSearchParams();
  if (flags.refresh) params.set("refresh", "true");
  if (flags.all) params.set("all", "true");
  if (flags.clientSuccess) params.set("clientSuccess", "true");
  const search = params.toString();
  return `${env.NEXT_PUBLIC_API_URL}/api/fun/activity${search ? `?${search}` : ""}`;
}

interface ActivityScope {
  accountAddress: string | undefined;
  getAuthHeaders: GetAuthHeaders;
  subaccountAddress: string | undefined;
}

async function fetchFunActivity(
  { accountAddress, getAuthHeaders, subaccountAddress }: ActivityScope,
  flags: ActivityFlags,
): Promise<FunActivityResponse> {
  let activity: FunActivityResponse;
  try {
    const response = await authenticatedRequest.get({
      schema: FunActivityEnvelopeSchema,
      url: activityUrl(flags),
      getAuthHeaders,
      subaccountAddress,
    });
    activity = response.data.data;
  } catch (error) {
    throw toFunPaymentError(error);
  }
  assertAnsweredFor(accountAddress, activity.accountAddress);
  return activity;
}

export function useFunActivityQuery({
  accountAddress,
  getAuthHeaders,
  subaccountAddress,
  enabled = true,
  all = false,
}: {
  accountAddress: string | undefined;
  getAuthHeaders: GetAuthHeaders;
  subaccountAddress: string | undefined;
  enabled?: boolean;
  /** Every displayable item rather than the recent window. */
  all?: boolean;
}) {
  return useQuery({
    queryKey: all ? funActivityAllQueryKey(accountAddress) : funActivityQueryKey(accountAddress),
    queryFn: () => fetchFunActivity({ accountAddress, getAuthHeaders, subaccountAddress }, { all }),
    // Cookie-first, so not gated on `getAuthHeaders`; SIWA is only needed on a 401.
    enabled: enabled && Boolean(accountAddress),
    refetchInterval: (query) => {
      if (isTerminalFunError(query.state.error)) return ERROR_BACKOFF_MS;
      const activity = query.state.data;
      if (!activity) return false;
      if (activity.anyOpen && activity.items.some(isMovingFunPayment)) return OPEN_POLL_MS;
      // Open items that all need support wait on people, not on the next step.
      if (activity.anyOpen || activity.serverPolling) return WATCHING_POLL_MS;
      return false;
    },
    retry: funRetry(all ? "fun-activity-all" : "fun-activity", 3),
  });
}

/** The signed-in account's projected Fun payments, shared with every other reader of the key. */
export function useFunPayments({ enabled = true }: { enabled?: boolean } = {}) {
  const { accountAddress, subaccountAddress } = useAuthState();
  const { getAuthHeaders } = useWriteDexSdk();
  return useFunActivityQuery({ accountAddress, getAuthHeaders, subaccountAddress, enabled });
}

/**
 * Puts the server's answer for one payment into the account's activity, replacing whichever item
 * shares an id or alias with it. With nothing cached it re-reads instead of inventing a list.
 */
export async function seedFunPaymentItem(
  queryClient: QueryClient,
  accountAddress: string | undefined,
  item: FunPaymentItem,
): Promise<void> {
  const queryKey = funActivityQueryKey(accountAddress);
  const ids = new Set([item.id, ...item.aliases]);
  // A read still in flight predates this answer and would overwrite it when it lands.
  await queryClient.cancelQueries({ queryKey, exact: true });
  const seeded = queryClient.setQueryData<FunActivityResponse>(queryKey, (activity) => {
    if (!activity) return activity;
    const known = (other: FunPaymentItem) =>
      ids.has(other.id) || other.aliases.some((id) => ids.has(id));
    const index = activity.items.findIndex(known);
    const items =
      index === -1
        ? [item, ...activity.items]
        : activity.items.flatMap((other, at) =>
            at === index ? [item] : known(other) ? [] : [other],
          );
    return { ...activity, items, anyOpen: items.some(isOpenFunPayment) };
  });
  if (!seeded) void queryClient.invalidateQueries({ queryKey, exact: true });
  void queryClient.invalidateQueries({ queryKey: funActivityAllQueryKey(accountAddress) });
}

/** Writes a fresh server answer over the account's activity and re-reads its show-all list. */
export async function storeFunActivity(
  queryClient: QueryClient,
  accountAddress: string,
  activity: FunActivityResponse,
): Promise<void> {
  const queryKey = funActivityQueryKey(accountAddress);
  // A plain read still in flight predates this answer.
  await queryClient.cancelQueries({ queryKey, exact: true });
  queryClient.setQueryData<FunActivityResponse>(queryKey, activity);
  void queryClient.invalidateQueries({ queryKey: funActivityAllQueryKey(accountAddress) });
}

/**
 * Asks the server to poll Fun now. `clientSuccess` tells it the checkout reported success, so a
 * charge that never produces a record gets flagged. Never throws.
 */
export function useRefreshFunActivity() {
  const queryClient = useQueryClient();
  const { accountAddress, subaccountAddress } = useAuthState();
  const { getAuthHeaders } = useWriteDexSdk();

  return useCallback(
    async ({ clientSuccess = false }: { clientSuccess?: boolean } = {}): Promise<void> => {
      if (!accountAddress) return;
      try {
        const activity = await fetchFunActivity(
          { accountAddress, getAuthHeaders, subaccountAddress },
          { refresh: true, clientSuccess },
        );
        await storeFunActivity(queryClient, accountAddress, activity);
      } catch (error) {
        if (isTerminalFunError(error) || error instanceof AuthNotReadyError) return;
        Logger.error(error, { feature: "fun-activity-refresh" });
      }
    },
    [accountAddress, getAuthHeaders, queryClient, subaccountAddress],
  );
}
